// Executes the extraction pipeline for one call under a given prompt set + model,
// returning a structured extraction plus token usage (plan §7).
//   - mockExecutor: deterministic, offline; derives a plausible candidate from the
//     golden and perturbs it by a synthetic model/prompt "quality". Powers the
//     mock-first UI so runs work end-to-end for review. Its scores are NOT evidence:
//     higher prompt versions score better by construction. `simulatedExecutor` takes
//     the error rate from the caller; the seed runs use it to script a history.
//   - localPeopleExecutor: dev only. Runs production's People stage on a real model
//     through the dev-server model proxy (src/bin/opsEvalRunnerPlugin.ts), sending the
//     prompt TEXT of the chosen version. Only the People stage is scored.
//   - localPipelineExecutor: dev only. Runs a whole pipeline on a real model: production's
//     5 stages, or the single prompt when the prompt set names one (pipeline/pipelines.ts).
// Full-pipeline execution waits on a non-persisting, prompt-override extraction
// capability in the backend (plan §8 dep #1). Do NOT call `/api/ops/extraction/extract`:
// it re-runs production extraction and saves over the call's live `call.ai`.

import type { DatasetItem, GoldenPerson } from 'app/components/ops-datasets/mockData'
import { PATIENT_TYPES } from 'app/components/ops-datasets/mockData'
import { MOCK_VERSIONS } from 'app/components/ops-prompts/mockData'
import { OPS_EVAL_MODEL_PATH, type OpsEvalModelRequest, type OpsEvalModelResponse } from './localModelContract'
import { PEOPLE_SCHEMA } from './pipeline/schemas'
import { runSinglePipeline, runStagedPipeline } from './pipeline/pipelines'
import type { ModelCall, PipelineResult } from './pipeline/types'
import type { Extraction } from './scoring'
import { PIPELINE_STAGES, type Model, type PipelineStage, type PromptSet, type TokenUsage } from './types'
import { SCORED_STAGES } from './scoring'

export type ExecutorInput = { item: DatasetItem; promptSet: PromptSet; model: Model; temperature: number }
export type ExecutorOutput = { extraction: Extraction; usage: TokenUsage }
export type Executor = (input: ExecutorInput) => Promise<ExecutorOutput>

// --- Deterministic helpers (no Math.random, so runs are reproducible) ---

function hash(str: string): number {
  let h = 2166136261
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

/** A 0..1 pseudo-random drawn deterministically from a seed string. */
function rand(seed: string): number {
  return hash(seed) / 4294967295
}

const MODEL_BASE_ERROR: Record<string, number> = {
  'gpt-5.5': 0.04,
  'gpt-5.4': 0.05,
  'gpt-5.4-mini': 0.07,
  'gpt-5.4-nano': 0.1,
  'gpt-5.2': 0.05,
  'gpt-5.1': 0.06,
  'gpt-5': 0.06,
  'gpt-5-mini': 0.1,
  'gpt-5-nano': 0.12,
  'gpt-4.1': 0.08,
  'gpt-4.1-mini': 0.1,
  'gpt-4.1-nano': 0.16,
  'gpt-4o': 0.08,
  'gpt-4o-mini': 0.14,
  'gpt-4-turbo': 0.12,
  'gpt-4': 0.15,
  'gpt-3.5-turbo': 0.28,
}

/** Synthetic per-run error rate: better models and higher prompt versions => fewer
 *  errors, so improving a prompt visibly moves accuracy in the mock. */
function errorRate(model: Model, promptSet: PromptSet): number {
  const base = MODEL_BASE_ERROR[model] ?? 0.15
  const versionSum = SCORED_STAGES.reduce((sum, stage) => sum + (promptSet[stage] ?? 1), 0)
  const promptBonus = Math.min(0.6, versionSum / 120)
  return Math.max(0.02, base * (1 - promptBonus))
}

function otherPatientType(current: string, seed: string): string {
  const options = PATIENT_TYPES.filter((type) => type !== current)
  return options[hash(seed) % options.length]
}

function perturbPerson(person: GoldenPerson, rate: number, seedBase: string): GoldenPerson {
  const next = structuredClone(person)
  if (rand(`${seedBase}:name`) < rate) next.name = next.name ? `${next.name} Jr` : `unknown person`
  if (rand(`${seedBase}:ptype`) < rate) next.patientType = otherPatientType(next.patientType, `${seedBase}:ptype2`)
  if (rand(`${seedBase}:caller`) < rate * 0.4) next.isCaller = !next.isCaller
  if (rand(`${seedBase}:sched`) < rate) {
    next.appointmentScheduled = !next.appointmentScheduled
    if (!next.appointmentScheduled) {
      next.appointmentDate = undefined
      next.appointmentTime = undefined
    }
  }
  if (next.appointmentScheduled && next.appointmentDate && rand(`${seedBase}:date`) < rate) {
    next.appointmentDate = `${next.appointmentDate.slice(0, -1)}9` // nudge the day
  }
  if (rand(`${seedBase}:out`) < rate) {
    next.outcomes = next.outcomes.length > 1 ? next.outcomes.slice(1) : [...next.outcomes, `Other`]
  }
  return next
}

/** A simulated executor whose per-field error rate comes from `rateFor`. The seed runs
 *  use it to script a history; everything else uses `mockExecutor`. */
export const simulatedExecutor =
  (rateFor: (input: ExecutorInput) => number): Executor =>
  async (input) => {
    const { item, promptSet, model } = input
    const rate = rateFor(input)
    const stageSeed = SCORED_STAGES.map((stage) => promptSet[stage] ?? 1).join(`-`)
    const versionSeed = promptSet.single === undefined ? stageSeed : `single${promptSet.single}`
    const seedBase = `${item.id}:${model}:${versionSeed}`

    const golden = item.golden
    let people = golden.people.map((person, i) => perturbPerson(person, rate, `${seedBase}:p${i}`))

    // Occasionally drop or duplicate a person (people-count error).
    if (people.length > 1 && rand(`${seedBase}:drop`) < rate * 0.4) {
      people = people.slice(0, -1)
    } else if (rand(`${seedBase}:dup`) < rate * 0.25) {
      people = [...people, structuredClone(people[people.length - 1])]
    }

    const extraction: Extraction = {
      answerer: rand(`${seedBase}:answerer`) < rate ? undefined : golden.answerer,
      channel: golden.channel,
      callType: rand(`${seedBase}:ctype`) < rate ? undefined : golden.callType,
      people,
    }

    // Deterministic token usage: transcript size drives prompt tokens (5 stage calls),
    // output size drives completion tokens, with slight per-model variance.
    const transcriptTokens = Math.ceil(item.transcriptSnapshot.length / 4)
    const modelVariance = 1 + (hash(`${seedBase}:tok`) % 20) / 100
    const promptTokens = Math.round((transcriptTokens * 5 + 2000) * modelVariance)
    const completionTokens = Math.round((golden.people.length * 45 + 140) * modelVariance)

    return { extraction, usage: { promptTokens, completionTokens } }
  }

export const mockExecutor: Executor = simulatedExecutor(({ model, promptSet }) => errorRate(model, promptSet))

function promptBody(key: PipelineStage | `single`, version: number): string {
  const found = MOCK_VERSIONS.find((entry) => entry.promptKey === key && entry.version === version)
  if (!found) throw new Error(`No prompt text for ${key} v${version}`)
  return found.body
}

/** Calls the dev-server model proxy with `model`. A failed request fails the run. */
function modelCall(model: Model): ModelCall {
  return async ({ system, user, parameters }) => {
    const request: OpsEvalModelRequest = { model, system, user, parameters }
    const response = await window.fetch(OPS_EVAL_MODEL_PATH, {
      method: `POST`,
      headers: { 'Content-Type': `application/json` },
      body: JSON.stringify(request),
    })
    const data = (await response.json()) as OpsEvalModelResponse
    if (`error` in data) throw new Error(`Model call failed: ${data.error}`)
    return data
  }
}

/** The People stage alone, as production runs it (stages/people.ts). */
export const localPeopleExecutor: Executor = async ({ item, promptSet, model }) => {
  // Production skips the model for an empty transcript and returns this placeholder.
  if (!item.transcriptSnapshot.trim()) {
    const placeholder: GoldenPerson = {
      name: ``,
      isCaller: true,
      patientType: `Other`,
      appointmentRequested: false,
      appointmentScheduled: false,
      outcomes: [],
    }
    return { extraction: { people: [placeholder] }, usage: { promptTokens: 0, completionTokens: 0 } }
  }
  const date = new Date(item.callDate)
  const callDateText = new Date(date.getTime() + date.getTimezoneOffset() * 60_000).toDateString()
  const { args, usage } = await modelCall(model)({
    system: promptBody(`people`, promptSet.people),
    user: `Call date: ${callDateText}

Transcription:
${item.transcriptSnapshot}`,
    parameters: PEOPLE_SCHEMA,
  })
  const raw = (args?.people as Record<string, string | boolean | null>[] | undefined) ?? [
    { name: null, isCaller: true, patientType: `Other` },
  ]
  // Production maps a missing or `unknown` name to no name; the scorer treats both alike.
  // The People stage produces no appointment or outcome fields; a People-stage run does
  // not score those stages.
  const people: GoldenPerson[] = raw.map((person) => ({
    name: String(person.name ?? ``),
    isCaller: Boolean(person.isCaller),
    patientType: String(person.patientType ?? `Other`),
    relationship: (person.relationship as string | null) ?? undefined,
    phone: (person.phone as string | null) ?? undefined,
    email: (person.email as string | null) ?? undefined,
    birthDate: (person.birthDate as string | null) ?? undefined,
    appointmentRequested: false,
    appointmentScheduled: false,
    outcomes: [],
  }))
  return { extraction: { people }, usage }
}

/** The pipeline's saved output, in the shape the scorer compares with a golden. */
function toExtraction(result: PipelineResult): Extraction {
  const { output } = result
  return {
    answerer: output.answerer,
    channel: output.channel,
    callType: result.callType,
    people: output.people.map((person) => ({
      name: person.name ?? ``,
      isCaller: person.isCaller ?? false,
      patientType: person.patientType ?? `Other`,
      relationship: person.relationship,
      phone: person.phone,
      email: person.email,
      birthDate: person.birthDate,
      appointmentRequested: person.appointmentRequested ?? false,
      appointmentScheduled: person.appointmentScheduled ?? false,
      appointmentDate: person.appointmentDate,
      appointmentTime: person.appointmentTime,
      treatment: person.treatment,
      serviceRequested: person.serviceRequested,
      reasonForNotScheduling: person.reasonForNotScheduling,
      outcomes: person.outcomes ?? [],
      insuranceProvider: person.insuranceProvider,
      referral: person.referral,
    })),
    summary: output.summary,
    actionItems: output.actionItems,
  }
}

/** A whole pipeline: the single prompt when the set names one, else production's 5 stages. */
export const localPipelineExecutor: Executor = async ({ item, promptSet, model }) => {
  const input = { transcript: item.transcriptSnapshot, callDate: item.callDate }
  const call = modelCall(model)
  const result =
    promptSet.single === undefined
      ? await runStagedPipeline(
          input,
          Object.fromEntries(PIPELINE_STAGES.map((stage) => [stage, promptBody(stage, promptSet[stage])])) as Record<
            PipelineStage,
            string
          >,
          call,
        )
      : await runSinglePipeline(input, promptBody(`single`, promptSet.single), call)
  return { extraction: toExtraction(result), usage: result.usage }
}
